/**
 * /internal/integrations → Roadmap tab (VRG-694-Internal.Integrations.Roadmap, epic VRG-688-Integrations).
 *
 * Our master list of candidate integrations, ranked by hand. It reads the same
 * `integration_requests` rows as the Votes tab (migration 0146 added the roadmap
 * columns to them), so a customer vote and our rank are one row with one live count:
 * customer suggestions appear here unranked until someone gives them a priority.
 *
 * Seeded from vera-inc `product/integrations-landscape.md` §3 and the 2026-10-13
 * priority decision (`apps/api/src/features/integration-requests/integration-roadmap.seed.ts`).
 *
 * Built on the shared `Table` (working-conventions §28): sorting, the filter drawer,
 * the search box and CSV export come from it. Inline edits — priority, fit, role,
 * rationale — save on commit through `PATCH /internal/integration-requests/:id/roadmap`
 * and are optimistic (see `useUpdateRoadmapIntegration`).
 */
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Box, InputBase, Popover, Stack, Typography, useTheme } from '@mui/material'
import type { GridSortModel } from '@mui/x-data-grid-pro'
import { CheckIcon, MapIcon } from 'lucide-react'

import { Button, EMPTY_VALUE, MENU_PAPER_SX, TextArea, menuPaperChromeSx } from '@repo/ui'
import { ActionDropdown, EmptyState, IntegrationLogo, StatusChip, Table } from 'components'
import { statusFilterOptions } from 'components/input/filters/StatusChipFilter'
import { membershipFilter, nullsLastComparator } from 'components/input/filters/tableOperators'
import { INTEGRATION_FIT_GRADES, INTEGRATION_REQUEST_STATUSES } from 'constants/statuses'
import { ROADMAP_CATEGORY_LABELS } from 'constants/integrations'
import { useIntegrationRoadmap, useUpdateRoadmapIntegration } from 'hooks/api'
import { useSnackbar } from 'contexts'
import type {
  RoadmapFit,
  RoadmapIntegration,
  RoadmapRole,
  RoadmapUpdate,
} from 'services/integrationRequestsApi'

// ─── Constants ──────────────────────────────────────────────────────────────

const FIT_ORDER: RoadmapFit[] = ['A', 'A-', 'B+', 'B', 'B-', 'C', 'D']
const ROLE_ORDER: RoadmapRole[] = ['connect', 'supply', 'rival']
const ROLE_LABELS: Record<RoadmapRole, string> = {
  connect: 'Connect',
  supply: 'Supply',
  rival: 'Rival',
}
const STATUS_ORDER = Object.keys(INTEGRATION_REQUEST_STATUSES)

type EonLabel = 'Listed' | 'Not listed' | 'Not checked'
const EON_OPTIONS: EonLabel[] = ['Listed', 'Not listed', 'Not checked']

/** Neutral chip styling for category tags, reusing `StatusChip` (§36) rather than a new pill. */
const CATEGORY_CHIPS = Object.fromEntries(
  Object.entries(ROADMAP_CATEGORY_LABELS).map(([key, label]) => [
    key,
    { ...INTEGRATION_FIT_GRADES.D, label },
  ]),
)

const DEFAULT_SORT: GridSortModel = [{ field: 'internalPriority', sort: 'asc' }]

type Row = RoadmapIntegration & {
  fitKey: RoadmapFit | 'unrated'
  eonLabel: EonLabel
  veraRefsText: string
}

const categoryLabel = (c: string) =>
  ROADMAP_CATEGORY_LABELS[c as keyof typeof ROADMAP_CATEGORY_LABELS] ?? c

/** Empty values LAST in both directions (shared: components/input/filters/tableOperators). */
const nullsLast = <T,>(rank: (row: Row) => T | null, compare: (a: T, b: T) => number) =>
  nullsLastComparator<Row, T>(rank, compare)

/** `isAnyOf` as set membership (shared: components/input/filters/tableOperators). */
const membership = (itemsOf: (row: Row) => string[]) => membershipFilter<Row>(itemsOf)

/**
 * The filter drawer (MultiSelectFilter) renders a multiSelect column's `renderCell` once per
 * OPTION, with `{ value: option, filter: true, row: { [field]: option } }` — a synthetic row
 * holding one scalar. A cell that reads the real row's arrays would throw there, and the
 * page's ErrorBoundary turns that into a reload. So each such column renders its option
 * label in filter mode.
 */
type CellParams = { row: Row; value?: unknown; filter?: boolean }
const optionLabel = (label: string) => <Typography variant="body3">{label}</Typography>

// The grid owns arrow/space/enter for cell navigation; an editor inside a cell must
// keep its keystrokes (same fix as GtmTradeShowsPage's PriceCheckCell).
const stopGridKeys = (e: React.KeyboardEvent) => e.stopPropagation()

// ─── Cells ──────────────────────────────────────────────────────────────────

const PriorityCell = ({ row, onSave }: { row: Row; onSave: (p: RoadmapUpdate) => void }) => {
  const theme = useTheme()
  const [draft, setDraft] = useState(row.internalPriority?.toString() ?? '')
  const ref = useRef<HTMLInputElement>(null)
  useEffect(() => setDraft(row.internalPriority?.toString() ?? ''), [row.internalPriority])

  const commit = () => {
    const trimmed = draft.trim()
    const next = trimmed === '' ? null : Number(trimmed)
    if (next !== null && (!Number.isInteger(next) || next < 1)) {
      setDraft(row.internalPriority?.toString() ?? '')
      return
    }
    if (next !== row.internalPriority) onSave({ internalPriority: next })
  }

  return (
    <InputBase
      inputRef={ref}
      value={draft}
      placeholder="—"
      onChange={(e) => setDraft(e.target.value.replace(/[^0-9]/g, '').slice(0, 4))}
      onBlur={commit}
      onKeyDown={(e) => {
        stopGridKeys(e)
        if (e.key === 'Enter') ref.current?.blur()
        if (e.key === 'Escape') {
          setDraft(row.internalPriority?.toString() ?? '')
          setTimeout(() => ref.current?.blur())
        }
      }}
      inputProps={{
        inputMode: 'numeric',
        'aria-label': `Priority for ${row.name}`,
        'data-testid': `roadmap-priority-${row.slug}`,
      }}
      sx={{
        width: 56,
        height: 32,
        fontSize: 14,
        fontWeight: row.internalPriority ? 600 : 400,
        borderRadius: '8px',
        border: `1px solid ${theme.palette.primary[100]}`,
        bgcolor: 'background.paper',
        transition: 'border-color 0.15s, box-shadow 0.15s',
        '& input': { textAlign: 'center', p: 0 },
        '&:hover': { borderColor: theme.palette.brand?.[600] },
        '&.Mui-focused': {
          borderColor: theme.palette.brand?.[600],
          boxShadow: `0 0 0 3px ${theme.palette.brand?.['600-6%']}`,
        },
      }}
    />
  )
}

/** Menu-row icon column (§21): a check on the current value, an equal-width spacer otherwise. */
const pickIcon = (selected: boolean) =>
  selected ? <CheckIcon size={16} strokeWidth={1.5} /> : <Box sx={{ width: 16 }} />

const FitCell = ({ row, onSave }: { row: Row; onSave: (p: RoadmapUpdate) => void }) => (
  <Box onKeyDown={stopGridKeys}>
    <ActionDropdown
      trigger={({ onClick }) => (
        <Box
          component="button"
          type="button"
          onClick={onClick}
          aria-label={`Fit for ${row.name}`}
          sx={{ all: 'unset', cursor: 'pointer', display: 'inline-flex', borderRadius: '8px',
            '&:focus-visible': { outline: (t) => `2px solid ${t.palette.brand?.[600]}`, outlineOffset: 1 } }}
        >
          <StatusChip statuses={INTEGRATION_FIT_GRADES} status={row.fitKey} size="small" />
        </Box>
      )}
      items={[
        ...FIT_ORDER.map((fit) => ({
          label: INTEGRATION_FIT_GRADES[fit].label,
          icon: pickIcon(row.fit === fit),
          onClick: () => fit !== row.fit && onSave({ fit }),
        })),
        {
          label: 'Clear grade',
          divider: true,
          icon: pickIcon(row.fit === null),
          onClick: () => row.fit !== null && onSave({ fit: null }),
        },
      ]}
    />
  </Box>
)

const RolesCell = ({ row, onSave }: { row: Row; onSave: (p: RoadmapUpdate) => void }) => (
  <Box onKeyDown={stopGridKeys} sx={{ minWidth: 0 }}>
    <ActionDropdown
      trigger={({ onClick }) => (
        <Box
          component="button"
          type="button"
          onClick={onClick}
          aria-label={`Role for ${row.name}`}
          sx={{ all: 'unset', cursor: 'pointer', borderRadius: '6px', px: 1, py: 0.5,
            '&:hover': { bgcolor: 'action.hover' },
            '&:focus-visible': { outline: (t) => `2px solid ${t.palette.brand?.[600]}`, outlineOffset: 1 } }}
        >
          <Typography variant="body3" color={row.roles.length ? 'text.primary' : 'text.secondary'} noWrap>
            {row.roles.length ? row.roles.map((r) => ROLE_LABELS[r]).join(' · ') : EMPTY_VALUE}
          </Typography>
        </Box>
      )}
      items={ROLE_ORDER.map((role) => {
        const has = row.roles.includes(role)
        return {
          label: ROLE_LABELS[role],
          icon: pickIcon(has),
          onClick: () =>
            onSave({ roles: has ? row.roles.filter((r) => r !== role) : [...row.roles, role] }),
        }
      })}
    />
  </Box>
)

/**
 * Rationale: one truncated line in the row; click to read it whole and edit it in a
 * popover. A popover rather than a growing row, because variable row heights would
 * make the ranked list jump as people read it.
 */
const RationaleCell = ({ row, onSave }: { row: Row; onSave: (p: RoadmapUpdate) => void }) => {
  const theme = useTheme()
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  const [draft, setDraft] = useState(row.rationale ?? '')
  const open = Boolean(anchor)
  const dirty = draft.trim() !== (row.rationale ?? '')

  return (
    <>
      <Box
        component="button"
        type="button"
        onClick={(e: React.MouseEvent<HTMLElement>) => {
          setDraft(row.rationale ?? '')
          setAnchor(e.currentTarget)
        }}
        aria-label={`Rationale for ${row.name}`}
        sx={{ all: 'unset', cursor: 'pointer', minWidth: 0, maxWidth: '100%', borderRadius: '6px',
          '&:hover .roadmap-rationale': { textDecoration: 'underline' },
          '&:focus-visible': { outline: `2px solid ${theme.palette.brand?.[600]}`, outlineOffset: 1 } }}
      >
        <Typography className="roadmap-rationale" variant="body3" color="text.secondary" noWrap>
          {row.rationale || 'Add rationale'}
        </Typography>
      </Box>
      <Popover
        open={open}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: { sx: { ...MENU_PAPER_SX, ...menuPaperChromeSx(theme), width: 420, maxWidth: '90vw', p: 4 } },
        }}
        onKeyDown={stopGridKeys}
      >
        <Stack spacing={3}>
          <Typography variant="subtitle2">{row.name}</Typography>
          <TextArea
            label="Rationale"
            value={draft}
            onValueChange={setDraft}
            minRows={4}
            maxRows={12}
          />
          <Stack direction="row" spacing={3} justifyContent="flex-end">
            <Button variant="secondary" onClick={() => setAnchor(null)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={!dirty}
              onClick={() => {
                onSave({ rationale: draft.trim() || null })
                setAnchor(null)
              }}
            >
              Save
            </Button>
          </Stack>
        </Stack>
      </Popover>
    </>
  )
}

const NameCell = ({ row }: { row: Row }) => (
  <Stack direction="row" alignItems="center" spacing={2} sx={{ minWidth: 0 }}>
    <IntegrationLogo
      src={row.logo}
      alt={row.name}
      size={36}
      fallback={
        <Typography variant="body4" fontWeight={600}>
          {row.name.charAt(0).toUpperCase()}
        </Typography>
      }
    />
    <Stack sx={{ minWidth: 0 }}>
      <Typography variant="body3" fontWeight={500} noWrap title={row.name}>
        {row.name}
      </Typography>
      {row.source === 'customer' && (
        <Typography variant="body4" color="text.secondary" noWrap>
          Customer suggestion
        </Typography>
      )}
    </Stack>
  </Stack>
)

// ─── Tab ────────────────────────────────────────────────────────────────────

export const RoadmapTab = () => {
  const { data = [], isLoading } = useIntegrationRoadmap()
  const update = useUpdateRoadmapIntegration()
  const { open: notification } = useSnackbar()

  const save = (row: Row) => (patch: RoadmapUpdate) =>
    update.mutate(
      { id: row.id, patch },
      {
        onError: (e: any) =>
          notification({
            type: 'error',
            title: e?.response?.data?.message || e?.message || `Could not update ${row.name}.`,
          }),
      },
    )

  const rows: Row[] = useMemo(
    () =>
      data.map((r) => ({
        ...r,
        fitKey: r.fit ?? 'unrated',
        eonLabel: r.eonListed === null ? 'Not checked' : r.eonListed ? 'Listed' : 'Not listed',
        veraRefsText: r.veraRefs.join(', '),
      })),
    [data],
  )

  const categoryOptions = useMemo(
    () =>
      Object.keys(ROADMAP_CATEGORY_LABELS).filter((c) => rows.some((r) => r.categories.includes(c))),
    [rows],
  )

  const columns = useMemo(
    () => [
      {
        field: 'internalPriority',
        headerName: 'Priority',
        // §49: sized for the header + sort icon, not the 1–4 digit values.
        width: 128,
        type: 'number',
        align: 'left',
        headerAlign: 'left',
        filterable: false,
        getSortComparator: nullsLast((r) => r.internalPriority, (a: number, b: number) => a - b),
        renderCell: ({ row }: { row: Row }) => <PriorityCell row={row} onSave={save(row)} />,
      },
      {
        field: 'name',
        headerName: 'Integration',
        width: 240,
        type: 'string',
        filterable: false,
        renderCell: ({ row }: { row: Row }) => <NameCell row={row} />,
      },
      {
        field: 'categories',
        headerName: 'Categories',
        width: 260,
        type: 'multiSelect',
        options: categoryOptions,
        sortOptions: false,
        getOptionLabel: (o: unknown) => categoryLabel(String(o)),
        filterValueGetter: (_v: unknown, row: Row) => (row ? row.categories : []),
        filterOperators: membership((row) => row.categories),
        valueGetter: (_v: unknown, row: Row) => (row ? row.categories.map(categoryLabel).join(', ') : ''),
        getSortComparator: nullsLast(
          (r) => (r.categories.length ? categoryLabel(r.categories[0]) : null),
          (a: string, b: string) => a.localeCompare(b),
        ),
        renderCell: ({ row, value, filter }: CellParams) =>
          filter ? (
            optionLabel(categoryLabel(String(value)))
          ) : row.categories.length ? (
            <Stack direction="row" spacing={1} sx={{ minWidth: 0, overflow: 'hidden' }}>
              {row.categories.map((c) => (
                <StatusChip key={c} statuses={CATEGORY_CHIPS} status={c} size="small" />
              ))}
            </Stack>
          ) : (
            EMPTY_VALUE
          ),
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 150,
        type: 'statusChip',
        options: statusFilterOptions(INTEGRATION_REQUEST_STATUSES),
        sortComparator: (a: string, b: string) => STATUS_ORDER.indexOf(a) - STATUS_ORDER.indexOf(b),
        renderCell: ({ row }: { row: Row }) => (
          <StatusChip statuses={INTEGRATION_REQUEST_STATUSES} status={row.status} size="small" />
        ),
      },
      {
        field: 'fitKey',
        headerName: 'Fit',
        width: 120,
        type: 'statusChip',
        options: statusFilterOptions(INTEGRATION_FIT_GRADES),
        getSortComparator: nullsLast(
          (r) => (r.fit ? FIT_ORDER.indexOf(r.fit) : null),
          (a: number, b: number) => a - b,
        ),
        renderCell: ({ row }: { row: Row }) => <FitCell row={row} onSave={save(row)} />,
      },
      {
        field: 'voteCount',
        headerName: 'Votes',
        width: 96,
        type: 'number',
        align: 'left',
        headerAlign: 'left',
        filterable: false,
        renderCell: ({ row }: { row: Row }) => (
          <Typography variant="body3" color={row.voteCount ? 'text.primary' : 'text.secondary'}>
            {row.voteCount}
          </Typography>
        ),
      },
      {
        field: 'eonLabel',
        headerName: 'EON-listed',
        width: 132,
        type: 'multiSelect',
        options: EON_OPTIONS,
        sortOptions: false,
        renderCell: ({ row, value, filter }: CellParams) =>
          filter ? optionLabel(String(value)) : (
          <Typography variant="body3" color={row.eonListed ? 'text.primary' : 'text.secondary'}>
            {row.eonListed === null ? EMPTY_VALUE : row.eonListed ? 'Yes' : 'No'}
          </Typography>
        ),
      },
      {
        field: 'roles',
        headerName: 'Role',
        width: 150,
        type: 'multiSelect',
        options: ROLE_ORDER,
        sortOptions: false,
        getOptionLabel: (o: unknown) => ROLE_LABELS[o as RoadmapRole] ?? String(o),
        filterValueGetter: (_v: unknown, row: Row) => (row ? row.roles : []),
        filterOperators: membership((row) => row.roles),
        valueGetter: (_v: unknown, row: Row) => (row ? row.roles.map((r) => ROLE_LABELS[r]).join(', ') : ''),
        renderCell: ({ row, value, filter }: CellParams) =>
          filter ? (
            optionLabel(ROLE_LABELS[value as RoadmapRole] ?? String(value))
          ) : (
            <RolesCell row={row} onSave={save(row)} />
          ),
      },
      {
        field: 'veraRefsText',
        headerName: 'Vera refs',
        width: 180,
        type: 'string',
        filterable: false,
        renderCell: ({ row }: { row: Row }) => (
          <Typography variant="body3" color={row.veraRefs.length ? 'text.primary' : 'text.secondary'} noWrap title={row.veraRefsText}>
            {row.veraRefsText || EMPTY_VALUE}
          </Typography>
        ),
      },
      {
        field: 'rationale',
        headerName: 'Rationale',
        width: 420,
        type: 'string',
        filterable: false,
        sortable: false,
        renderCell: ({ row }: { row: Row }) => <RationaleCell row={row} onSave={save(row)} />,
      },
    ],
    // `save` closes over the mutation, which is stable for the component's life.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [categoryOptions],
  )

  return (
    <Table
      fillBottom
      loading={isLoading}
      rows={rows}
      columns={columns}
      getRowId={(row: Row) => row.id}
      defaultSortModel={DEFAULT_SORT}
      exportFilename="integrations-roadmap"
      emptyState={
        <EmptyState
          title="No integrations match"
          text="Clear the search or a filter to see the whole roadmap."
          icon={<MapIcon strokeWidth={1} size={32} />}
        />
      }
    />
  )
}
